// SPDX-License-Identifier: AGPL-3.0-or-later

import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import {captureNativeSystemAudioTrack} from '@app/features/voice/utils/NativeAudioCaptureBridge';
import {i18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {LocalAudioTrack, Room, RoomEvent, Track} from 'livekit-client';

/** Publishes playback separately from the user's microphone, without publishing video. */
export class CompanionSystemAudio {
	private stream: MediaStream | null = null;
	private room: Room | null = null;
	private epoch = 0;
	private paused = false;

	constructor(private readonly onFailure: (error: unknown) => void) {}

	async prepare(): Promise<void> {
		this.disconnect();
		const epoch = this.epoch;
		let stream: MediaStream;
		try {
			stream = getElectronAPI()
				? new MediaStream([await captureNativeSystemAudioTrack()])
				: await navigator.mediaDevices.getDisplayMedia({
						video: {frameRate: 1},
						audio: {echoCancellation: false, noiseSuppression: false, autoGainControl: false},
						systemAudio: 'include',
						selfBrowserSurface: 'exclude',
					} as DisplayMediaStreamOptions);
		} catch (error) {
			if (error instanceof DOMException && error.name === 'NotAllowedError') {
				throw new Error(i18n._(msg({message: 'Audio sharing was cancelled. Start again and allow audio sharing.'})));
			}
			throw new Error(i18n._(msg({message: 'System audio is unavailable. Choose another audio source.'})), {
				cause: error,
			});
		}
		if (epoch !== this.epoch) {
			stream.getTracks().forEach((track) => track.stop());
			throw new DOMException('Companion audio capture cancelled', 'AbortError');
		}
		const track = stream.getAudioTracks()[0];
		if (track?.readyState !== 'live' || stream.getTracks().some((value) => value.readyState !== 'live')) {
			stream.getTracks().forEach((value) => value.stop());
			throw new Error(i18n._(msg({message: 'No shared audio. Select a tab or screen and enable audio sharing.'})));
		}
		this.stream = stream;
		track.enabled = false;
		const onEnded = () => {
			if (this.stream !== stream) return;
			this.disconnect();
			this.onFailure(new Error(i18n._(msg({message: 'System audio sharing stopped.'}))));
		};
		for (const sharedTrack of stream.getTracks()) sharedTrack.addEventListener('ended', onEnded);
	}

	async connect(connection: {endpoint: string; token: string}): Promise<void> {
		const track = this.stream?.getAudioTracks()[0];
		if (track?.readyState !== 'live') throw new Error(i18n._(msg({message: 'System audio sharing stopped.'})));
		const epoch = this.epoch;
		const room = new Room();
		this.room = room;
		room.on(RoomEvent.Disconnected, () => {
			if (this.room !== room) return;
			this.disconnect();
			this.onFailure(new Error(i18n._(msg({message: 'System audio sharing stopped.'}))));
		});
		await room.connect(connection.endpoint, connection.token, {autoSubscribe: false});
		if (epoch !== this.epoch) {
			await room.disconnect();
			throw new DOMException('Companion audio capture cancelled', 'AbortError');
		}
		// A separate private participant lets the existing agent transcribe playback as the other side.
		track.enabled = !this.paused;
		await room.localParticipant.publishTrack(new LocalAudioTrack(track), {source: Track.Source.Microphone});
		if (epoch !== this.epoch) await room.disconnect();
	}

	setPaused(paused: boolean): void {
		this.paused = paused;
		for (const track of this.stream?.getAudioTracks() ?? []) track.enabled = !paused;
	}

	disconnect(): void {
		this.epoch += 1;
		const room = this.room;
		const stream = this.stream;
		this.room = null;
		this.stream = null;
		this.paused = false;
		stream?.getTracks().forEach((track) => {
			track.enabled = false;
			track.stop();
		});
		if (room) void room.disconnect().catch(() => {});
	}
}
